import { get } from "lodash-es";
import React, { useCallback, useState } from "react";
import { FormText } from "reactstrap";
import { TextInput } from "../components/inputs/final_form/TextInput";
import { LoadingIcon } from "../components/LoadingIcon";
import { api } from "../util/api";
import { useDebounced } from "../util/hooks";

const fetchSuggestedPayor = async (val) => {
  try {
    const response = await api.get(`/request_drafts/prefix_mapping.json?member_id=${val}`);
    return response.data;
  } catch (e) {
    console.error(e);
    return null;
  }
};

const checkPayorSupport = async (payor) => {
  if (!payor?.id) {
    return false;
  }

  const req = await api.get(`/payors/check_support?id=${payor.id}`);
  return req.data.is_supported;
};

export const InsuranceMemberIdInput = ({ selectedPayorId, input, onMatchFound, clearSecondaryInsuranceValues, ...props }) => {
  const { onChange, value } = input;
  const [checkingMatchingPayors, setCheckingMatchingPayors] = useState(false);
  const [matchingPayor, setMatchingPayor] = useState(null);
  const [matchingPayorSupported, setMatchingPayorSupported] = useState(false);
  const [memberIdValue, setMemberIdValue] = useState(value);

  const debouncedFetchSuggestedPayor = useDebounced(fetchSuggestedPayor, 300);

  const handleChange = useCallback(
    async (val) => {
      setMemberIdValue(val);

      if (onChange) {
        onChange(val);
      }

      if (val.length < 1) {
        setMatchingPayor(null);
        setMatchingPayorSupported(false);
        clearSecondaryInsuranceValues();
        return;
      }

      setCheckingMatchingPayors(true);

      try {
        const matchingPayors = await debouncedFetchSuggestedPayor(val);
        const suggestedPayor = get(matchingPayors, "0.payor", null);
        const isPayorSupported = await checkPayorSupport(suggestedPayor);

        // Keep unsupported matches for user feedback.
        setMatchingPayor(suggestedPayor);
        setMatchingPayorSupported(isPayorSupported);

        if (suggestedPayor && isPayorSupported) {
          onMatchFound(suggestedPayor.public_id, suggestedPayor.name);
        } else {
          onMatchFound(null, null);
        }
      } catch (err) {
        console.error(err);
        setMatchingPayor(null);
        setMatchingPayorSupported(false);
        onMatchFound(null, null);
      } finally {
        setCheckingMatchingPayors(false);
      }
    },
    [onChange, setCheckingMatchingPayors, onMatchFound, clearSecondaryInsuranceValues, debouncedFetchSuggestedPayor]
  );

  const suggestedPayorName = matchingPayor?.name;
  const suggestedPayorId = matchingPayor?.public_id;

  return (
    <>
      <TextInput {...props} input={{ ...input, value: memberIdValue, onChange: (e) => handleChange(e.target.value) }}>
        <>
          {checkingMatchingPayors ? (
            <FormText>
              <LoadingIcon /> Checking for known payors...
            </FormText>
          ) : (
            <>
              {suggestedPayorName &&
                (matchingPayorSupported ? (
                  suggestedPayorId === selectedPayorId && (
                    <FormText>
                      The member id you provided is associated with <strong>{suggestedPayorName}</strong>. The Primary Insurance has been
                      changed to <strong>{suggestedPayorName}</strong>.
                    </FormText>
                  )
                ) : (
                  <FormText>
                    The member id you provided is associated with <strong>{suggestedPayorName}</strong>. This Payor is not supported by your
                    organization.
                  </FormText>
                ))}
            </>
          )}
        </>
      </TextInput>
    </>
  );
};
